/** Typed API client for flima_rebirth.

This is the ONLY place fetch is called. All components use this client.
 */

import { ApiError, type CatalogueEntry, type GemeindeOption, type GeoJSONFeatureCollection, type RegionConfig } from "../types";

const BASE_URL = import.meta.env.VITE_API_URL || "/api";

async function fetchJson<T>(path: string): Promise<T> {
  try {
    const response = await fetch(`${BASE_URL}${path}`);
    if (!response.ok) {
      let detail = response.statusText;
      try {
        const body = await response.json();
        detail = body.detail || body.error || detail;
      } catch {
        // Response body is not JSON — use status text
      }
      throw new ApiError(response.status, detail);
    }
    return (await response.json()) as T;
  } catch (err) {
    if (err instanceof ApiError) {
      throw err;
    }
    // Network error or other fetch failure
    throw new ApiError(0, err instanceof Error ? err.message : String(err));
  }
}

export const apiClient = {
  /** GET /api/config — region configuration. */
  async getConfig(): Promise<RegionConfig> {
    return fetchJson<RegionConfig>("/config");
  },

  /** GET /api/gemeinden — all municipality boundaries. */
  async getGemeinden(): Promise<GeoJSONFeatureCollection> {
    return fetchJson<GeoJSONFeatureCollection>("/gemeinden");
  },

  /** GET /api/gemeinden/names — municipality search options ({uid, name, region}). */
  async getGemeindeNames(): Promise<GemeindeOption[]> {
    return fetchJson<GemeindeOption[]>("/gemeinden/names");
  },

  /** GET /api/gemeinden/locations — lightweight municipality marker points. */
  async getGemeindeLocations(): Promise<GeoJSONFeatureCollection> {
    return fetchJson<GeoJSONFeatureCollection>("/gemeinden/locations");
  },

  /** GET /api/gemeinden/{value} — one municipality boundary (value is a UID or name). */
  async getGemeinde(gemeinde: string): Promise<GeoJSONFeatureCollection> {
    return fetchJson<GeoJSONFeatureCollection>(
      `/gemeinden/${encodeURIComponent(gemeinde)}`,
    );
  },

  /** GET /api/species?gemeinde=X — species habitats clipped to municipality. */
  async getSpecies(gemeinde: string): Promise<GeoJSONFeatureCollection> {
    return fetchJson<GeoJSONFeatureCollection>(`/species?gemeinde=${encodeURIComponent(gemeinde)}`);
  },

  /** GET /api/measures?gemeinde=X&months=... — measure zones with overlap detection. */
  async getMeasures(gemeinde: string, months?: number[]): Promise<GeoJSONFeatureCollection> {
    let path = `/measures?gemeinde=${encodeURIComponent(gemeinde)}`;
    if (months && months.length > 0) {
      path += `&months=${months.join(",")}`;
    }
    return fetchJson<GeoJSONFeatureCollection>(path);
  },

  /** GET /api/layers/{layer}?gemeinde=X — base layer clipped to municipality. */
  async getLayer(layer: string, gemeinde: string): Promise<GeoJSONFeatureCollection> {
    return fetchJson<GeoJSONFeatureCollection>(
      `/layers/${layer}?gemeinde=${encodeURIComponent(gemeinde)}`,
    );
  },

  /** GET /api/catalogue — all measures with descriptions + images. */
  async getCatalogue(): Promise<CatalogueEntry[]> {
    return fetchJson<CatalogueEntry[]>("/catalogue");
  },
};
